import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common'; // Import CommonModule for NgIf
import { IconValuesType } from './icon.component.types';

// Define the allowed icon sizes
export type IconSize =
  | 'small' // Small icon size
  | 'base' // Base icon size (default)
  | 'medium' // Medium icon size
  | 'large' // Large icon size
  | 'extra-large'; // Extra-large icon size

// Define the allowed color variants for the icon
export type Color =
  | 'default' // Default color (usually inherits currentColor)
  | 'primary' // Primary theme color
  | 'secondary' // Secondary theme color
  | 'success' // Success theme color (e.g., green)
  | 'info' // Informational theme color (e.g., blue)
  | 'warning' // Warning theme color (e.g., yellow/orange)
  | 'error'
  | 'inactive'
  | 'tertiary'
  | 'white'
  | 'inherit'; // Inherit color from parent // Error theme color (e.g., red)

@Component({
  selector: 'hyperkit-icon', // The tag name to use this component in templates
  standalone: true, // This component is standalone and does not require any other components
  imports: [CommonModule], // Add CommonModule to imports
  templateUrl: './icon.component.html', // HTML file for the component
  styleUrls: ['./icon.component.scss'], // SCSS file for the component's styles
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  // The ID of the SVG symbol in the sprite
  @Input() icon!: IconValuesType;

  // The size of the icon, based on the predefined IconSize types
  @Input() size?: IconSize = 'small';

  // The color variant of the icon, defaulting to 'default'
  @Input() color?: Color = 'default';

  // Accessibility role to describe the element as an image
  @Input() role?: string = 'img';

  // Accessibility label for screen readers
  @Input() ariaLabel?: string | null = null;

  // The fixed path to the SVG sprite file
  readonly spritePath: string = '../../../assets/icons.svg';


  /**
   * A getter to dynamically compute the classes for the icon.
   * Combines size and color-related classes based on the inputs provided.
   *
   * @returns {string[]} An array of classes to apply to the icon element.
   */
  get classes(): string[] {
    return [
      `icon-${this.size}`, // Class for the size of the icon
      `icon-color-${this.color}`, // Class for the color of the icon
    ];
  }
}
